import React, { useState, useEffect } from 'react';
import {
    LucideDownload,
    LucideRefreshCcw,
    LucideClipboardList,
    LucideFrown
} from 'lucide-react';
const API_URL = process.env.REACT_APP_API_URL;

// Bu bileşen, "Ekibimize Katılın" başvurularını listelemek için bir yönetici paneli sağlar.
const App = () => {
    // Başvuruları, yüklenme durumunu ve hataları yönetmek için state'ler.
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Backend'den başvuru verilerini çekmek için useEffect hook'u kullanıyoruz.
    useEffect(() => {
        const fetchApplications = async () => {
            try {
                // Backend'den tüm başvuruları getiren API endpoint'ine GET isteği gönderiyoruz.
                const response = await fetch(`${API_URL}/api/applications`);
                
                if (response.ok) {
                    const data = await response.json();
                    setApplications(data);
                } else {
                    // Hata durumunda, backend'den gelen mesajı alıyoruz.
                    const errorData = await response.json();
                    setError(errorData.detail || 'Veriler yüklenirken bir hata oluştu.');
                }
            } catch (err) {
                // Ağ hatası veya sunucuya bağlanamama durumunda hata mesajı.
                setError('Ağa bağlanırken bir sorun oluştu. Lütfen backend sunucusunun çalıştığından emin olun.');
            } finally {
                // Veri çekme işlemi bittiğinde yüklenme durumunu false yapıyoruz.
                setLoading(false);
            }
        };

        fetchApplications();
    }, []); // Boş dependency array ile bileşen yüklendiğinde bir kez çalışır.

    return (
        <div className="min-h-screen bg-slate-900 text-slate-200 font-sans p-4 sm:p-8">
            <div className="w-full max-w-7xl mx-auto">
                {/* Panel Başlığı */}
                <div className="text-center mb-12">
                    <h1 className="text-5xl md:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
                        Başvuru Yönetici Paneli
                    </h1>
                    <p className="text-lg text-slate-400 mt-2">
                        Ekibimize yapılan tüm başvuruları burada görüntüleyebilirsiniz.
                    </p>
                </div>

                {/* Yükleme Durumu */}
                {loading && (
                    <div className="flex items-center justify-center p-8 text-cyan-400">
                        <LucideRefreshCcw className="animate-spin mr-3 h-8 w-8" />
                        <p className="text-xl font-medium">Başvurular yükleniyor...</p>
                    </div>
                )}

                {/* Hata Mesajı */}
                {error && (
                    <div className="flex items-center justify-center p-8 bg-red-800 text-red-200 rounded-xl shadow-md">
                        <LucideFrown className="w-6 h-6 mr-3" />
                        <p className="font-medium">{error}</p>
                    </div>
                )}

                {/* Veri Yok Mesajı */}
                {!loading && !error && applications.length === 0 && (
                    <div className="flex items-center justify-center p-8 bg-slate-800 text-slate-400 rounded-xl shadow-md">
                        <LucideClipboardList className="w-6 h-6 mr-3" />
                        <p className="font-medium">Henüz bir başvuru bulunmamaktadır.</p>
                    </div>
                )}

                {/* Başvuru Tablosu */}
                {!loading && !error && applications.length > 0 && (
                    <div className="overflow-x-auto bg-slate-800 rounded-2xl shadow-xl">
                        <table className="min-w-full divide-y divide-slate-700">
                            <thead className="bg-slate-700">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-slate-300 uppercase tracking-wider">
                                        Ad Soyad
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-slate-300 uppercase tracking-wider">
                                        E-posta
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-slate-300 uppercase tracking-wider">
                                        Pozisyon
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-slate-300 uppercase tracking-wider">
                                        Başvuru Tarihi
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-slate-300 uppercase tracking-wider">
                                        CV
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-slate-800 divide-y divide-slate-700">
                                {applications.map((app) => (
                                    <tr key={app.id} className="hover:bg-slate-700 transition-colors duration-200">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-100">
                                            {app.name}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400">
                                            {app.email}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400">
                                            {app.department || 'Belirtilmemiş'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400">
                                            {/* Tarih formatı backend'den gelen veriye göre ayarlanabilir */}
                                            {new Date(app.created_at).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            {/* CV dosyasını indirmek için link. `cv_file_path` backend'den tam yolu içeriyor olmalı. */}
                                            {app.cv_file_path && (
                                                <a
                                                    href={`${API_URL}/download/${app.cv_file_path}`} // Backend'in dosya sunma yoluyla eşleşmeli
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center text-cyan-400 hover:text-cyan-300 transition-colors duration-200"
                                                >
                                                    <LucideDownload className="w-4 h-4 mr-1" />
                                                    CV İndir
                                                </a>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default App;
